import React, { useMemo } from 'react';

import withPDF from '../../hoc/withPDF';
import useCoverSheetOverview from '../../../hooks/useCoverSheetOverview';

import Conditional from '../../common/conditional';
import CustomUTMBreakdown from '../custom-utm/custom-utm-breakdown';
import CustomPAUTSummary from '../custom-paut/paut-summary';
import CustomInfoSheet from '../custom-general/custom-info-sheet';
import CustomCoverPage from '../custom-general/custom-cover-page';
import CustomGeneralPhotos from '../custom-general/custom-general-photos';
import CustomUTMReportPDF from '../custom-utm/custom-utm-report';
import IsometricDrawing from '../custom-utm/isometric-drawing';
import OverviewSheet from '../custom-general/overview-sheet';
import TOFDPlan from './tofd-plan';
import TOFDData from './tofd-data';

import { TOFDScanPlanItem } from './tofdScanPlanItems';
import { TOFDScanDataItem } from './tofdScanDataItems';

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

interface Props {
	coverData: models.ExxonMobilFVIPipingCoverData;
	drawing: models.PDFImageProps[];
	photoRecord: models.PDFImageProps[];
	projectDetails: models.Project;
	scanPlan: TOFDScanPlanItem[];
	scanData: TOFDScanDataItem[];
	utInspectionItems: models.UTMInspection[];
	utInspectionOverview: models.CustomUTMOverview;
	scanDrawing: models.PDFImageProps[];
    executiveSummary: string;
	reportTitle: string;
	tmlLocations: models.PDFImageProps[];
}

const CustomTOFDReport: React.FC<Props> = ({
	coverData,
	drawing,
    executiveSummary,
	photoRecord,
    scanData,
	scanPlan,
	utInspectionItems,
	utInspectionOverview,
	projectDetails,
	reportTitle,
	scanDrawing,
	tmlLocations,
}) => {
	const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: coverData?.scopeOfWork,
		},
		{
			label: '2. VT Procedure.',
			value: coverData?.vtProcedure,
		},
		{
			label: '3. Rope Access Code of Practice',
			value: coverData?.ropeAccessCodeOfPractice,
		},
		{
			label: '4. Work Procedure',
			value: coverData?.exxonMobilWorkProcedure,
		},
		{
			label: '5. RusselSmith Test Procedure',
			value: coverData?.russelsmithProcedure ?? '',
		},
	];

	const standardCriteriaItems: models.CoverDataItem[] = useMemo(() => {
		return coverData.coverData.map((item, index) => ({
			label: `${index + 1}. ${item.id}`,
			value: item.name.toString(),
		}));
	}, [coverData.coverData]);

	const overview = useCoverSheetOverview();

	return (
		<React.Fragment>
			<CustomCoverPage reportTitle={utils.getExtractedInspectionName(projectDetails.inspectionTypeName)} />

			<CustomInfoSheet
				projectDetails={projectDetails}
				workOrderNumber={coverData.workOrderNumber}
				reportTitle={reportTitle}
			/>

			<OverviewSheet
				overviewData={overview}
				referenceDocuments={referenceDocuments}
				standardCriteriaItems={standardCriteriaItems}
				projectDetails={projectDetails}
			/>

            <CustomPAUTSummary 
                executiveSummary={executiveSummary}
                pautDataItems={scanData}
            />

			<Conditional isVisible={utInspectionItems.length > 0}>
				<CustomUTMBreakdown
					corrosionAllowance={utInspectionOverview.corrosionAllowance}
					inspectionItems={utInspectionItems} 
				/>
			</Conditional>

            <TOFDPlan tofdPlanItems={scanPlan} />
            
            <TOFDData tofdDataItems={scanData} />

			<Conditional isVisible={scanDrawing.length > 0}>
				<IsometricDrawing isoImages={scanDrawing} label="Scan Drawing" />
			</Conditional>

			<Conditional isVisible={utInspectionItems.length > 0}>
				<CustomUTMReportPDF
					title="General Drawing"
					tmlLocations={tmlLocations}
					utInspectionItems={utInspectionItems}
					inspectionOverview={utInspectionOverview}
				/>
			</Conditional>

			<CustomGeneralPhotos label="Photographic Record" photos={photoRecord} />
		</React.Fragment>
	);
};

export default withPDF(CustomTOFDReport);
